import { map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { COUNTRY, HAS_VAT_IDENTIFIER, VAT_TYPE, VAT_AREA, CUSTOMER_TYPE, EU_MEMBERS, EFTA_MEMBERS } from '@red/data';

var ERROR_CODE;
(function (ERROR_CODE) {
    ERROR_CODE[ERROR_CODE["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
    ERROR_CODE[ERROR_CODE["FORBIDDEN"] = 403] = "FORBIDDEN";
    ERROR_CODE[ERROR_CODE["NOT_FOUND"] = 404] = "NOT_FOUND";
    ERROR_CODE[ERROR_CODE["INTERNAL_SERVER_ERROR"] = 500] = "INTERNAL_SERVER_ERROR";
    ERROR_CODE[ERROR_CODE["GENERAL_ERROR"] = 1500] = "GENERAL_ERROR";
    ERROR_CODE[ERROR_CODE["MISSING_BANK_DETAILS"] = 124002] = "MISSING_BANK_DETAILS";
    ERROR_CODE[ERROR_CODE["MISSING_BANK_DETAILS_SALARY"] = 104001] = "MISSING_BANK_DETAILS_SALARY";
    ERROR_CODE[ERROR_CODE["INVALID_BANK_DETAILS_SALARY"] = 104002] = "INVALID_BANK_DETAILS_SALARY";
    ERROR_CODE[ERROR_CODE["REGISTRATION_INVALID_FIELD"] = 1340911] = "REGISTRATION_INVALID_FIELD";
    ERROR_CODE[ERROR_CODE["INVOICE_CURRENCY_MISMATCH"] = 64091] = "INVOICE_CURRENCY_MISMATCH";
    ERROR_CODE[ERROR_CODE["INVALID_USER_IDENTIFIER"] = 34001] = "INVALID_USER_IDENTIFIER";
    // Frontend gateway
    ERROR_CODE[ERROR_CODE["FGW_MISSING_CLIENT_HEADER"] = 254001] = "FGW_MISSING_CLIENT_HEADER";
    ERROR_CODE[ERROR_CODE["FGW_UKNOWN_CLIENT"] = 254011] = "FGW_UKNOWN_CLIENT";
    ERROR_CODE[ERROR_CODE["FGW_MISSING_AUTH_HEADER"] = 254002] = "FGW_MISSING_AUTH_HEADER";
    ERROR_CODE[ERROR_CODE["FGW_INVALID_AUTH_HEADER"] = 254012] = "FGW_INVALID_AUTH_HEADER";
    ERROR_CODE[ERROR_CODE["FGW_NO_ACTIVE_COMPANY"] = 254041] = "FGW_NO_ACTIVE_COMPANY";
    // Client gateway
    ERROR_CODE[ERROR_CODE["SESSION_INVALID"] = 40101] = "SESSION_INVALID";
    ERROR_CODE[ERROR_CODE["ACCESS_DENIED"] = 40301] = "ACCESS_DENIED";
})(ERROR_CODE || (ERROR_CODE = {}));

class ServerError extends Error {
    constructor(error) {
        let msg;
        if (typeof error === 'object' && error) {
            const innerError = ServerError._isInnerError(error) ? error['error'] : error;
            if (typeof innerError === 'string') {
                msg = innerError;
            }
            else {
                msg = innerError.message;
            }
            super(msg);
            let isProgressEventError;
            try {
                // eslint-disable-next-line  no-undef
                isProgressEventError = innerError instanceof ProgressEvent;
            }
            catch (e) {
                isProgressEventError = false;
            }
            if (isProgressEventError && typeof error.status === 'number') {
                this._errorCode = error.status;
            }
            else if (!isProgressEventError && ServerError._isMappedError(innerError)) {
                this._errorCode = innerError.errorCode;
                this._traceId = innerError.traceId;
            }
            else if (typeof error.status === 'number') {
                this._errorCode = error.status;
            }
        }
        else {
            msg = error;
            super(msg);
        }
        Object.setPrototypeOf(this, ServerError.prototype);
    }
    get errorCode() {
        return this._errorCode;
    }
    get traceId() {
        return this._traceId;
    }
    static _isInnerError(error) {
        // Angular puts response as an inner error
        return error['error'] !== undefined;
    }
    static _isMappedError(error) {
        return error['errorCode'] !== undefined || error['traceId'] !== undefined;
    }
}

class BaseServiceClient {
    constructor(transport) {
        this._transport = transport;
    }
    _delete(url, options, mapTo) {
        return this._transport.delete(url, options).pipe(map(this._mapResponse(mapTo)));
    }
    _get(url, options, mapTo) {
        return this._transport.get(url, options).pipe(map(this._mapResponse(mapTo)));
    }
    _post(url, body, options, mapTo) {
        return this._transport.post(url, body, options).pipe(map(this._mapResponse(mapTo)));
    }
    _put(url, body, options, mapTo) {
        return this._transport.put(url, body, options).pipe(map(this._mapResponse(mapTo)));
    }
    _mapResponse(mapTo) {
        return (resp) => {
            if (mapTo) {
                return new mapTo(resp.body);
            }
            return resp;
        };
    }
}

var HTTP_METHOD;
(function (HTTP_METHOD) {
    HTTP_METHOD["GET"] = "GET";
    HTTP_METHOD["POST"] = "POST";
    HTTP_METHOD["PUT"] = "PUT";
    HTTP_METHOD["DELETE"] = "DELETE";
    HTTP_METHOD["OPTIONS"] = "OPTIONS";
    HTTP_METHOD["HEAD"] = "HEAD";
    HTTP_METHOD["PATCH"] = "PATCH";
})(HTTP_METHOD || (HTTP_METHOD = {}));
class HttpFetch {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    request(request, options) {
        return throwError(new Error('Transport is abstract class. Extend it and implement own request method'));
    }
    delete(url, options) {
        const request = { method: HTTP_METHOD.DELETE, url: url };
        return this.request(request, options);
    }
    get(url, options) {
        const request = { method: HTTP_METHOD.GET, url: url };
        return this.request(request, options);
    }
    post(url, body, options) {
        const request = { method: HTTP_METHOD.POST, url: url, body: body };
        return this.request(request, options);
    }
    put(url, body, options) {
        const request = { method: HTTP_METHOD.PUT, url: url, body: body };
        return this.request(request, options);
    }
}

/* eslint-disable */
class VatValidator {
    static validate(country, vatId) {
        if (!VatValidator._validateVatIdCountry(country, vatId)) {
            return false;
        }
        const c = VatValidator._territoryCountry(country);
        const name = `_${c.toString().toLowerCase()}`;
        if (VatValidator.hasOwnProperty(name) && typeof VatValidator[name] === 'function') {
            return VatValidator[name](vatId);
        }
        return null;
    }
    static _validateVatIdCountry(country, vatId) {
        const c = VatValidator._territoryCountry(country).toString().toLowerCase();
        if (country === COUNTRY.GREECE && vatId.substr(0, 2) === 'EL') {
            return true;
        }
        return c === vatId.substr(0, c.length).toLowerCase();
    }
    static _territoryCountry(country) {
        if (country === COUNTRY.ISLEOFMAN) {
            return COUNTRY.UNITEDKINGDOM;
        }
        return country;
    }
    static _at(a) {
        if ((/^ATU[0-9]{8}$/.test(a) && (a = a.substr(2)), !/^U[0-9]{8}$/.test(a)))
            return !1;
        a = a.substr(1);
        for (var b = 0, c = [1, 2, 1, 2, 1, 2, 1], d = 0, e = 0; 7 > e; e++)
            (d = parseInt(a.charAt(e), 10) * c[e]), d > 9 && (d = Math.floor(d / 10) + (d % 10)), (b += d);
        return (b = 10 - ((b + 4) % 10)), 10 === b && (b = 0), b + '' === a.substr(7, 1);
    }
    static _be(a) {
        if ((/^BE[0]{0,1}[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0]{0,1}[0-9]{9}$/.test(a)))
            return !1;
        if ((9 === a.length && (a = '0' + a), '0' === a.substr(1, 1)))
            return !1;
        const b = parseInt(a.substr(0, 8), 10) + parseInt(a.substr(8, 2), 10);
        return b % 97 === 0;
    }
    static _bg(a) {
        if ((/^BG[0-9]{9,10}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9,10}$/.test(a)))
            return !1;
        let b = 0, c = 0;
        if (9 === a.length) {
            for (c = 0; 8 > c; c++)
                b += parseInt(a.charAt(c), 10) * (c + 1);
            if (((b %= 11), 10 === b))
                for (b = 0, c = 0; 8 > c; c++)
                    b += parseInt(a.charAt(c), 10) * (c + 3);
            return (b %= 10), b + '' === a.substr(8);
        }
        if (10 === a.length) {
            const d = function (a) {
                let b = parseInt(a.substr(0, 2), 10) + 1900, c = parseInt(a.substr(2, 2), 10), d = parseInt(a.substr(4, 2), 10);
                if ((c > 40 ? ((b += 100), (c -= 40)) : c > 20 && ((b -= 100), (c -= 20)),
                    !VatValidator._date(b, c, d)))
                    return !1;
                for (var e = 0, f = [2, 4, 8, 5, 10, 9, 7, 3, 6], g = 0; 9 > g; g++)
                    e += parseInt(a.charAt(g), 10) * f[g];
                return (e = (e % 11) % 10), e + '' === a.substr(9, 1);
            }, e = function (a) {
                for (var b = 0, c = [21, 19, 17, 13, 11, 9, 7, 3, 1], d = 0; 9 > d; d++)
                    b += parseInt(a.charAt(d), 10) * c[d];
                return (b %= 10), b + '' === a.substr(9, 1);
            }, f = function (a) {
                for (var b = 0, c = [4, 3, 2, 7, 6, 5, 4, 3, 2], d = 0; 9 > d; d++)
                    b += parseInt(a.charAt(d), 10) * c[d];
                return (b = 11 - (b % 11)), 10 === b ? !1 : (11 === b && (b = 0), b + '' === a.substr(9, 1));
            };
            return d(a) || e(a) || f(a);
        }
        return !1;
    }
    static _br(a) {
        if ('' === a)
            return !0;
        const b = a.replace(/[^\d]+/g, '');
        if ('' === b || 14 !== b.length)
            return !1;
        if ('00000000000000' === b ||
            '11111111111111' === b ||
            '22222222222222' === b ||
            '33333333333333' === b ||
            '44444444444444' === b ||
            '55555555555555' === b ||
            '66666666666666' === b ||
            '77777777777777' === b ||
            '88888888888888' === b ||
            '99999999999999' === b)
            return !1;
        for (var c = b.length - 2, d = b.substring(0, c), e = b.substring(c), f = 0, g = c - 7, h = c; h >= 1; h--)
            (f += parseInt(d.charAt(c - h), 10) * g--), 2 > g && (g = 9);
        let i = 2 > f % 11 ? 0 : 11 - (f % 11);
        if (i !== parseInt(e.charAt(0), 10))
            return !1;
        for (c += 1, d = b.substring(0, c), f = 0, g = c - 7, h = c; h >= 1; h--)
            (f += parseInt(d.charAt(c - h), 10) * g--), 2 > g && (g = 9);
        return (i = 2 > f % 11 ? 0 : 11 - (f % 11)), i === parseInt(e.charAt(1), 10);
    }
    static _ch(a) {
        if ((/^CHE[0-9]{9}(MWST)?$/.test(a) && (a = a.substr(2)), !/^E[0-9]{9}(MWST)?$/.test(a)))
            return !1;
        a = a.substr(1);
        for (var b = 0, c = [5, 4, 3, 2, 7, 6, 5, 4], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return (b = 11 - (b % 11)), 10 === b ? !1 : (11 === b && (b = 0), b + '' === a.substr(8, 1));
    }
    static _cy(a) {
        if ((/^CY[0-5|9]{1}[0-9]{7}[A-Z]{1}$/.test(a) && (a = a.substr(2)), !/^[0-5|9]{1}[0-9]{7}[A-Z]{1}$/.test(a)))
            return !1;
        if ('12' === a.substr(0, 2))
            return !1;
        for (var b = 0, c = {
            0: 1,
            1: 0,
            2: 5,
            3: 7,
            4: 9,
            5: 13,
            6: 15,
            7: 17,
            8: 19,
            9: 21,
        }, d = 0; 8 > d; d++) {
            let e = parseInt(a.charAt(d), 10);
            d % 2 === 0 && (e = c[e + '']), (b += e);
        }
        const f = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[b % 26];
        return f + '' === a.substr(8, 1);
    }
    static _cz(a) {
        if ((/^CZ[0-9]{8,10}$/.test(a) && (a = a.substr(2)), !/^[0-9]{8,10}$/.test(a)))
            return !1;
        let b = 0, c = 0;
        if (8 === a.length) {
            if (a.charAt(0) + '' == '9')
                return !1;
            for (b = 0, c = 0; 7 > c; c++)
                b += parseInt(a.charAt(c), 10) * (8 - c);
            return (b = 11 - (b % 11)), 10 === b && (b = 0), 11 === b && (b = 1), b + '' === a.substr(7, 1);
        }
        if (9 === a.length && a.charAt(0) + '' == '6') {
            for (b = 0, c = 0; 7 > c; c++)
                b += parseInt(a.charAt(c + 1), 10) * (8 - c);
            return ((b = 11 - (b % 11)),
                10 === b && (b = 0),
                11 === b && (b = 1),
                (b = [8, 7, 6, 5, 4, 3, 2, 1, 0, 9, 10][b - 1]),
                b + '' === a.substr(8, 1));
        }
        if (9 === a.length || 10 === a.length) {
            let d = 1900 + parseInt(a.substr(0, 2), 10), e = (parseInt(a.substr(2, 2), 10) % 50) % 20, f = parseInt(a.substr(4, 2), 10);
            if (9 === a.length) {
                if ((d >= 1980 && (d -= 100), d > 1953))
                    return !1;
            }
            else
                1954 > d && (d += 100);
            if (!VatValidator._date(d, e, f))
                return !1;
            if (10 === a.length) {
                let g = parseInt(a.substr(0, 9), 10) % 11;
                return 1985 > d && (g %= 10), g + '' === a.substr(9, 1);
            }
            return !0;
        }
        return !1;
    }
    static _de(a) {
        return /^DE[0-9]{9}$/.test(a) && (a = a.substr(2)), /^[0-9]{9}$/.test(a) ? VatValidator._mod11And10(a) : !1;
    }
    static _dk(a) {
        if ((/^DK[0-9]{8}$/.test(a) && (a = a.substr(2)), !/^[0-9]{8}$/.test(a)))
            return !1;
        for (var b = 0, c = [2, 7, 6, 5, 4, 3, 2, 1], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 11 === 0;
    }
    static _ee(a) {
        if ((/^EE[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}$/.test(a)))
            return !1;
        for (var b = 0, c = [3, 7, 1, 3, 7, 1, 3, 7, 1], d = 0; 9 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 10 === 0;
    }
    static _es(a) {
        if ((/^ES[0-9A-Z][0-9]{7}[0-9A-Z]$/.test(a) && (a = a.substr(2)), !/^[0-9A-Z][0-9]{7}[0-9A-Z]$/.test(a)))
            return !1;
        const b = function (a) {
            const b = parseInt(a.substr(0, 8), 10);
            const c = 'TRWAGMYFPDXBNJZSQVHLCKE'[b % 23];
            return c + '' === a.substr(8, 1);
        }, c = function (a) {
            let b = ['XYZ'.indexOf(a.charAt(0)), a.substr(1)].join('');
            const c = parseInt(b, 10);
            b = 'TRWAGMYFPDXBNJZSQVHLCKE'[c % 23];
            return b + '' === a.substr(8, 1);
        }, d = function (a) {
            let b, c = a.charAt(0);
            if (-1 !== 'KLM'.indexOf(c))
                return ((b = parseInt(a.substr(1, 8), 10)),
                    (b = 'TRWAGMYFPDXBNJZSQVHLCKE'[b % 23]),
                    b + '' === a.substr(8, 1));
            if (-1 !== 'ABCDEFGHJNPQRSUVW'.indexOf(c)) {
                for (var d = 0, e = [2, 1, 2, 1, 2, 1, 2], f = 0, g = 0; 7 > g; g++)
                    (f = parseInt(a.charAt(g + 1), 10) * e[g]),
                        f > 9 && (f = Math.floor(f / 10) + (f % 10)),
                        (d += f);
                return ((d = 10 - (d % 10)),
                    10 === d && (d = 0),
                    d + '' === a.substr(8, 1) || 'JABCDEFGHI'[d] === a.substr(8, 1));
            }
            return !1;
        }, e = a.charAt(0);
        const v = /^[0-9]$/.test(e)
            ? {
                valid: b(a),
                type: 'DNI',
            }
            : /^[XYZ]$/.test(e)
                ? {
                    valid: c(a),
                    type: 'NIE',
                }
                : {
                    valid: d(a),
                    type: 'CIF',
                };
        return v.valid;
    }
    static _fi(a) {
        if ((/^FI[0-9]{8}$/.test(a) && (a = a.substr(2)), !/^[0-9]{8}$/.test(a)))
            return !1;
        for (var b = 0, c = [7, 9, 10, 5, 8, 4, 2, 1], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 11 === 0;
    }
    static _fr(a) {
        if ((/^FR[0-9A-Z]{2}[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9A-Z]{2}[0-9]{9}$/.test(a))) {
            return !1;
        }
        if (!VatValidator._luhn(a.substr(2))) {
            return !1;
        }
        if (/^[0-9]{2}$/.test(a.substr(0, 2))) {
            const substrIntStr = parseInt(a.substr(0, 2), 10).toString();
            return substrIntStr === (parseInt(a.substr(2) + '12', 10) % 97) + '';
        }
        let b;
        const c = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        return ((b = /^[0-9]{1}$/.test(a.charAt(0))
            ? 24 * c.indexOf(a.charAt(0)) + c.indexOf(a.charAt(1)) - 10
            : 34 * c.indexOf(a.charAt(0)) + c.indexOf(a.charAt(1)) - 100),
            (parseInt(a.substr(2), 10) + 1 + Math.floor(b / 11)) % 11 === b % 11);
    }
    static _gb(a) {
        if (((/^GB[0-9]{9}$/.test(a) ||
            /^GB[0-9]{12}$/.test(a) ||
            /^GBGD[0-9]{3}$/.test(a) ||
            /^GBHA[0-9]{3}$/.test(a) ||
            /^GB(GD|HA)8888[0-9]{5}$/.test(a)) &&
            (a = a.substr(2)),
            !(/^[0-9]{9}$/.test(a) ||
                /^[0-9]{12}$/.test(a) ||
                /^GD[0-9]{3}$/.test(a) ||
                /^HA[0-9]{3}$/.test(a) ||
                /^(GD|HA)8888[0-9]{5}$/.test(a))))
            return !1;
        const b = a.length;
        if (5 === b) {
            const c = a.substr(0, 2), d = parseInt(a.substr(2), 10);
            return ('GD' === c && 500 > d) || ('HA' === c && d >= 500);
        }
        if (11 === b && ('GD8888' === a.substr(0, 6) || 'HA8888' === a.substr(0, 6)))
            return ('GD' === a.substr(0, 2) && parseInt(a.substr(6, 3), 10) >= 500) ||
                ('HA' === a.substr(0, 2) && parseInt(a.substr(6, 3), 10) < 500)
                ? !1
                : parseInt(a.substr(6, 3), 10) % 97 === parseInt(a.substr(9, 2), 10);
        if (9 === b || 12 === b) {
            for (var e = 0, f = [8, 7, 6, 5, 4, 3, 2, 10, 1], g = 0; 9 > g; g++)
                e += parseInt(a.charAt(g), 10) * f[g];
            return (e %= 97), parseInt(a.substr(0, 3), 10) >= 100 ? 0 === e || 42 === e || 55 === e : 0 === e;
        }
        return !0;
    }
    static _gr(a) {
        if ((/^(GR|EL)[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}$/.test(a)))
            return !1;
        8 === a.length && (a = '0' + a);
        for (var b = 0, c = [256, 128, 64, 32, 16, 8, 4, 2], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return (b = (b % 11) % 10), b + '' === a.substr(8, 1);
    }
    static _el(a) {
        return this._gr(a);
    }
    static _hu(a) {
        if ((/^HU[0-9]{8}$/.test(a) && (a = a.substr(2)), !/^[0-9]{8}$/.test(a)))
            return !1;
        for (var b = 0, c = [9, 7, 3, 1, 9, 7, 3, 1], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 10 === 0;
    }
    static _hr(a) {
        return /^HR[0-9]{11}$/.test(a) && (a = a.substr(2)), /^[0-9]{11}$/.test(a) ? VatValidator._mod11And10(a) : !1;
    }
    static _ie(a) {
        if ((/^IE[0-9]{1}[0-9A-Z\*\+]{1}[0-9]{5}[A-Z]{1,2}$/.test(a) && (a = a.substr(2)),
            !/^[0-9]{1}[0-9A-Z\*\+]{1}[0-9]{5}[A-Z]{1,2}$/.test(a)))
            return !1;
        const b = function (a) {
            for (; a.length < 7;)
                a = '0' + a;
            for (var b = 'WABCDEFGHIJKLMNOPQRSTUV', c = 0, d = 0; 7 > d; d++)
                c += parseInt(a.charAt(d), 10) * (8 - d);
            return (c += 9 * b.indexOf(a.substr(7))), b[c % 23];
        };
        return /^[0-9]+$/.test(a.substr(0, 7))
            ? a.charAt(7) === b(a.substr(0, 7) + a.substr(8) + '')
            : -1 !== 'ABCDEFGHIJKLMNOPQRSTUVWXYZ+*'.indexOf(a.charAt(1))
                ? a.charAt(7) === b(a.substr(2, 5) + a.substr(0, 1) + '')
                : !0;
    }
    static _is(a) {
        return /^IS[0-9]{5,6}$/.test(a) && (a = a.substr(2)), /^[0-9]{5,6}$/.test(a);
    }
    static _it(a) {
        if ((/^IT[0-9]{11}$/.test(a) && (a = a.substr(2)), !/^[0-9]{11}$/.test(a)))
            return !1;
        if (0 === parseInt(a.substr(0, 7), 10))
            return !1;
        const b = parseInt(a.substr(7, 3), 10);
        return 1 > b || (b > 201 && 999 !== b && 888 !== b) ? !1 : VatValidator._luhn(a);
    }
    static _lt(a) {
        if ((/^LT([0-9]{7}1[0-9]{1}|[0-9]{10}1[0-9]{1})$/.test(a) && (a = a.substr(2)),
            !/^([0-9]{7}1[0-9]{1}|[0-9]{10}1[0-9]{1})$/.test(a)))
            return !1;
        let b, c = a.length, d = 0;
        for (b = 0; c - 1 > b; b++)
            d += parseInt(a.charAt(b), 10) * (1 + (b % 9));
        let e = d % 11;
        if (10 === e)
            for (d = 0, b = 0; c - 1 > b; b++)
                d += parseInt(a.charAt(b), 10) * (1 + ((b + 2) % 9));
        return (e = (e % 11) % 10), e + '' === a.charAt(c - 1);
    }
    static _lu(a) {
        return (/^LU[0-9]{8}$/.test(a) && (a = a.substr(2)),
            /^[0-9]{8}$/.test(a) ? (parseInt(a.substr(0, 6), 10) % 89) + '' === a.substr(6, 2) : !1);
    }
    static _lv(a) {
        if ((/^LV[0-9]{11}$/.test(a) && (a = a.substr(2)), !/^[0-9]{11}$/.test(a)))
            return !1;
        let b, c = parseInt(a.charAt(0), 10), d = 0, e = [], f = a.length;
        if (c > 3) {
            for (d = 0, e = [9, 1, 4, 8, 3, 10, 2, 5, 7, 6, 1], b = 0; f > b; b++)
                d += parseInt(a.charAt(b), 10) * e[b];
            return (d %= 11), 3 === d;
        }
        let g = parseInt(a.substr(0, 2), 10), h = parseInt(a.substr(2, 2), 10), i = parseInt(a.substr(4, 2), 10);
        if (((i = i + 1800 + 100 * parseInt(a.charAt(6), 10)), !VatValidator._date(i, h, g)))
            return !1;
        for (d = 0, e = [10, 5, 8, 4, 2, 1, 6, 3, 7, 9], b = 0; f - 1 > b; b++)
            d += parseInt(a.charAt(b), 10) * e[b];
        return (d = ((d + 1) % 11) % 10), d + '' === a.charAt(f - 1);
    }
    static _mt(a) {
        if ((/^MT[0-9]{8}$/.test(a) && (a = a.substr(2)), !/^[0-9]{8}$/.test(a)))
            return !1;
        for (var b = 0, c = [3, 4, 6, 7, 8, 9, 10, 1], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 37 === 0;
    }
    static _nl(a) {
        if ((/^NL[0-9]{9}B[0-9]{2}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}B[0-9]{2}$/.test(a)))
            return !1;
        for (var b = 0, c = [9, 8, 7, 6, 5, 4, 3, 2], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return (b %= 11), b > 9 && (b = 0), b + '' === a.substr(8, 1);
    }
    static _no(a) {
        if ((/^NO[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}$/.test(a)))
            return !1;
        for (var b = 0, c = [3, 2, 7, 6, 5, 4, 3, 2], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return (b = 11 - (b % 11)), 11 === b && (b = 0), b + '' === a.substr(8, 1);
    }
    static _pl(a) {
        if ((/^PL[0-9]{10}$/.test(a) && (a = a.substr(2)), !/^[0-9]{10}$/.test(a)))
            return !1;
        for (var b = 0, c = [6, 5, 7, 2, 3, 4, 5, 6, 7, -1], d = 0; 10 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return b % 11 === 0;
    }
    static _pt(a) {
        if ((/^PT[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}$/.test(a)))
            return !1;
        for (var b = 0, c = [9, 8, 7, 6, 5, 4, 3, 2], d = 0; 8 > d; d++)
            b += parseInt(a.charAt(d), 10) * c[d];
        return (b = 11 - (b % 11)), b > 9 && (b = 0), b + '' === a.substr(8, 1);
    }
    static _ro(a) {
        if ((/^RO[1-9][0-9]{1,9}$/.test(a) && (a = a.substr(2)), !/^[1-9][0-9]{1,9}$/.test(a)))
            return !1;
        for (var b = a.length, c = [7, 5, 3, 2, 1, 7, 5, 3, 2].slice(10 - b), d = 0, e = 0; b - 1 > e; e++)
            d += parseInt(a.charAt(e), 10) * c[e];
        return (d = ((10 * d) % 11) % 10), d + '' === a.substr(b - 1, 1);
    }
    static _ru(a) {
        if ((/^RU([0-9]{10}|[0-9]{12})$/.test(a) && (a = a.substr(2)), !/^([0-9]{10}|[0-9]{12})$/.test(a)))
            return !1;
        let b = 0;
        if (10 === a.length) {
            let c = 0, d = [2, 4, 10, 3, 5, 9, 4, 6, 8, 0];
            for (b = 0; 10 > b; b++)
                c += parseInt(a.charAt(b), 10) * d[b];
            return (c %= 11), c > 9 && (c %= 10), c + '' === a.substr(9, 1);
        }
        if (12 === a.length) {
            let e = 0, f = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8, 0], g = 0, h = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8, 0];
            for (b = 0; 11 > b; b++)
                (e += parseInt(a.charAt(b), 10) * f[b]), (g += parseInt(a.charAt(b), 10) * h[b]);
            return ((e %= 11),
                e > 9 && (e %= 10),
                (g %= 11),
                g > 9 && (g %= 10),
                e + '' === a.substr(10, 1) && g + '' === a.substr(11, 1));
        }
        return !1;
    }
    static _rs(a) {
        if ((/^RS[0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[0-9]{9}$/.test(a)))
            return !1;
        for (var b = 10, c = 0, d = 0; 8 > d; d++)
            (c = (parseInt(a.charAt(d), 10) + b) % 10), 0 === c && (c = 10), (b = (2 * c) % 11);
        return (b + parseInt(a.substr(8, 1), 10)) % 10 === 1;
    }
    static _se(a) {
        return (/^SE[0-9]{10}01$/.test(a) && (a = a.substr(2)),
            /^[0-9]{10}01$/.test(a) ? ((a = a.substr(0, 10)), VatValidator._luhn(a)) : !1);
    }
    static _si(a) {
        const b = a.match(/^(SI)?([1-9][0-9]{7})$/);
        if (!b)
            return !1;
        b[1] && (a = a.substr(2));
        for (var c = 0, d = [8, 7, 6, 5, 4, 3, 2], e = 0; 7 > e; e++)
            c += parseInt(a.charAt(e), 10) * d[e];
        return (c = 11 - (c % 11)), 10 === c && (c = 0), c + '' === a.substr(7, 1);
    }
    static _sk(a) {
        return (/^SK[1-9][0-9][(2-4)|(6-9)][0-9]{7}$/.test(a) && (a = a.substr(2)),
            /^[1-9][0-9][(2-4)|(6-9)][0-9]{7}$/.test(a) ? parseInt(a, 10) % 11 === 0 : !1);
    }
    static _ve(a) {
        if ((/^VE[VEJPG][0-9]{9}$/.test(a) && (a = a.substr(2)), !/^[VEJPG][0-9]{9}$/.test(a)))
            return !1;
        for (var b = {
            V: 4,
            E: 8,
            J: 12,
            P: 16,
            G: 20,
        }, c = b[a.charAt(0)], d = [3, 2, 7, 6, 5, 4, 3, 2], e = 0; 8 > e; e++)
            c += parseInt(a.charAt(e + 1), 10) * d[e];
        return (c = 11 - (c % 11)), (11 === c || 10 === c) && (c = 0), c + '' === a.substr(9, 1);
    }
    static _za(a) {
        return /^ZA4[0-9]{9}$/.test(a) && (a = a.substr(2)), /^4[0-9]{9}$/.test(a);
    }
    static _date(a, b, c, d) {
        if (isNaN(a) || isNaN(b) || isNaN(c))
            return !1;
        if (c.length > 2 || b.length > 2 || a.length > 4)
            return !1;
        if (((c = parseInt(c, 10)),
            (b = parseInt(b, 10)),
            (a = parseInt(a, 10)),
            1e3 > a || a > 9999 || 0 >= b || b > 12))
            return !1;
        const e = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if (((a % 400 === 0 || (a % 100 !== 0 && a % 4 === 0)) && (e[1] = 29), 0 >= c || c > e[b - 1]))
            return !1;
        if (d === !0) {
            const f = new Date(), g = f.getFullYear(), h = f.getMonth(), i = f.getDate();
            return g > a || (a === g && h > b - 1) || (a === g && b - 1 === h && i > c);
        }
        return !0;
    }
    /**
     * Implement Luhn validation algorithm
     * Credit to https://gist.github.com/ShirtlessKirk/2134376
     *
     * @see http://en.wikipedia.org/wiki/Luhn
     * @param {String} value
     * @returns {Boolean}
     */
    static _luhn(value) {
        let length = value.length, mul = 0, prodArr = [
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
            [0, 2, 4, 6, 8, 1, 3, 5, 7, 9],
        ], sum = 0;
        while (length--) {
            sum += prodArr[mul][parseInt(value.charAt(length), 10)];
            mul ^= 1;
        }
        return sum % 10 === 0 && sum > 0;
    }
    /**
     * Implement modulus 11, 10 (ISO 7064) algorithm
     *
     * @param {String} value
     * @returns {Boolean}
     */
    static _mod11And10(value) {
        let check = 5, length = value.length;
        for (let i = 0; i < length; i++) {
            check = ((((check || 10) * 2) % 11) + parseInt(value.charAt(i), 10)) % 10;
        }
        return check === 1;
    }
    /**
     * Implements Mod 37, 36 (ISO 7064) algorithm
     * Usages:
     * mod37And36('A12425GABC1234002M')
     * mod37And36('002006673085', '0123456789')
     *
     * @param {String} value
     * @param {String} [alphabet]
     * @returns {Boolean}
     */
    static _mod37And36(value, alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
        let modulus = alphabet.length, length = value.length, check = Math.floor(modulus / 2);
        for (let i = 0; i < length; i++) {
            check = ((((check || modulus) * 2) % (modulus + 1)) + alphabet.indexOf(value.charAt(i))) % modulus;
        }
        console.log(check);
        return check === 1;
    }
}

class Vat {
    static hasVatIdentifier(country) {
        return HAS_VAT_IDENTIFIER.indexOf(country) !== -1;
    }
    static percentageForVatType(vat) {
        let percentage = 0;
        switch (vat) {
            case VAT_TYPE.SE_25:
                percentage = 0.25;
                break;
            case VAT_TYPE.SE_12:
                percentage = 0.12;
                break;
            case VAT_TYPE.SE_06:
                percentage = 0.06;
                break;
            case VAT_TYPE.EU_00:
            case VAT_TYPE.SE_00:
                percentage = 0;
                break;
        }
        return percentage;
    }
    static vatArea(origin, vatId) {
        const target = Vat._getCountryForCode(vatId.substr(0, 2));
        if (origin === target) {
            return VAT_AREA.DOMESTIC_VAT;
        }
        if (Vat._isCountryMemberOfEU(origin) && Vat._isCountryMemberOfEU(target)) {
            return VAT_AREA.EU;
        }
        return VAT_AREA.NON_EU;
    }
    static vatIdentifierForCountry(country, identifier) {
        let vatId = identifier;
        switch (country) {
            case COUNTRY.SWEDEN:
                vatId = `${country}${identifier}01`;
                break;
        }
        return vatId;
    }
    static vatTypes(origin, vatId = 'SE', customerType = CUSTOMER_TYPE.COMPANY) {
        const target = Vat._getCountryForCode(vatId.substr(0, 2)) || COUNTRY.SWEDEN;
        if (target === COUNTRY.SWEDEN || customerType === CUSTOMER_TYPE.INDIVIDUAL) {
            return [VAT_TYPE.SE_25, VAT_TYPE.SE_12, VAT_TYPE.SE_06, VAT_TYPE.SE_00];
        }
        if (Vat._isCountryMemberOfEFTA(origin) && Vat._isCountryMemberOfEFTA(target)) {
            return [VAT_TYPE.EU_00];
        }
        return customerType === CUSTOMER_TYPE.COMPANY
            ? [VAT_TYPE.SE_00]
            : [VAT_TYPE.SE_25, VAT_TYPE.SE_12, VAT_TYPE.SE_06, VAT_TYPE.SE_00];
    }
    static validateVatId(country, vatId) {
        if (Vat._isCountryMemberOfEU(country)) {
            return !!EU_MEMBERS.filter(member => VatValidator.validate(member, vatId)).shift();
        }
        return VatValidator.validate(country, vatId);
    }
    static _isCountryMemberOfEFTA(country) {
        return EFTA_MEMBERS.indexOf(country) !== -1;
    }
    static _isCountryMemberOfEU(country) {
        return EU_MEMBERS.indexOf(country) !== -1;
    }
    static _getCountryForCode(code) {
        const key = Object.keys(COUNTRY)
            .filter(country => COUNTRY[country] === code)
            .shift();
        return key ? COUNTRY[key] : null;
    }
}

export { BaseServiceClient, ERROR_CODE, HTTP_METHOD, HttpFetch, ServerError, Vat, VatValidator };
